<template>
  <v-list
    group
    tag="v-list"
    flat
    dense
    class="py-0 d-flex flex-column"
    hide-on-leave
    leave-absolute
  >
    <v-skeleton-loader
      v-if="parentLoading || loading"
      key="skeleton"
      width="100%"
      height="100%"
      type="list-item-avatar-two-line@5"
    />
    <div
      v-for="(contact, index) in contacts"
      v-else-if="contacts.length"
      :key="contact.value"
    >
      <v-list-item
        class="rounded-lg px-2"
        :ripple="false"
        dense
        two-line
        style="opacity: 1 !important"
      >
        <v-list-item-content>
          <!-- Expand toggle only on the header so email/phone text can be selected to copy. -->
          <div
            class="d-flex justify-space-between"
            style="cursor: pointer"
            @click="contact.expanded = !contact.expanded"
          >
            <div
              class="d-flex align-center justify-start"
              style="gap: 16px"
            >
              <v-avatar
                tile
                class="rounded-lg"
                :color="
                  $vuetify.theme.isDark ? 'background lighten-1' : 'background'
                "
                :style="{
                  border: `1px solid ${$vuetify.theme.currentTheme.background3} !important`,
                }"
              >
                <v-icon color="iconColor">
                  mdi-account
                </v-icon>
              </v-avatar>
              <span class="d-flex justify-space-between flex-shrink-1">
                <div
                  class="align-start justify-start align-self-start flex-column d-flex text-body-2"
                  style="gap: 4px"
                >
                  <span class="font-weight-bold text-body-2">
                    {{ contact.fullName | capitalize }}
                    <span
                      v-if="contact.parent"
                      class="font-weight-regular text-caption text--disabled"
                    >
                      (via Parent Account)
                    </span></span>
                  <span
                    v-if="contact.title"
                    class="text-capitalize text-caption text--secondary font-weight-bold"
                    style="line-height: 0.85rem !important"
                    v-text="contact.title?.text"
                  />
                </div>
              </span>
            </div>
            <div
              class="d-flex justify-end align-center"
              style="gap: 12px"
            >
              <span
                :class="{
                  activeRotate: contact.expanded,
                }"
                style="display: flex; align-items: center; align-self: center"
              >
                <v-icon>mdi-chevron-down</v-icon>
              </span>
            </div>
          </div>
          <v-expand-transition
            leave-absolute
            hide-on-leave
          >
            <v-container
              v-if="contact.expanded"
              class="px-0 d-flex flex-column"
              style="gap: 4px; user-select: text"
              @click.stop
              @mousedown.stop
            >
              <v-row
                dense
                justify="space-between"
                align="center"
              >
                <v-col
                  cols="6"
                  sm="12"
                  lg="6"
                >
                  <div class="d-flex align-center justify-start">
                    <v-icon
                      small
                      left
                    >
                      mdi-email-outline
                    </v-icon>
                    <span class="text-body-2 font-weight-bold text--secondary">Email</span>
                  </div>
                </v-col>
                <v-col
                  cols="6"
                  sm="12"
                  lg="6"
                >
                  <div
                    v-if="contact?.email?.length > 0"
                    class="text-truncate"
                    style="gap: 4px; width: 100%; max-width: 100%"
                  >
                    <a
                      class="text-body-2 font-weight-medium review--text"
                      :href="`mailto:${contact.email.toLowerCase()}`"
                      target="_blank"
                      v-html="contact.email.toLowerCase()"
                    />
                  </div>
                  <div
                    v-else
                    class="d-flex align-center"
                    style="gap: 6px"
                  >
                    <span class="text-body-2 font-weight-black error--text">
                      Not Set
                    </span>
                    <v-icon
                      x-small
                      color="error"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </div>
                </v-col>
              </v-row>
              <v-row
                dense
                justify="space-between"
                align="center"
              >
                <v-col
                  cols="6"
                  sm="12"
                  lg="6"
                >
                  <div class="d-flex align-center justify-start">
                    <v-icon
                      small
                      left
                    >
                      mdi-phone-outline
                    </v-icon>
                    <span class="text-body-2 font-weight-bold text--secondary">Phone</span>
                  </div>
                </v-col>
                <v-col
                  cols="6"
                  sm="12"
                  lg="6"
                >
                  <div
                    v-if="contact?.phoneNumber"
                    class="text-truncate"
                    style="gap: 4px; width: 100%; max-width: 100%"
                  >
                    <a
                      class="text-body-2 font-weight-medium text-decoration-underline review--text"
                      :href="phoneHref(contact?.phoneNumber)"
                      target="_blank"
                      v-html="
                        isValidPhoneNumber(contact?.phoneNumber)
                          ? $options.filters.phoneNumber(contact?.phoneNumber)
                          : contact?.phoneNumber
                      "
                    />
                  </div>
                  <div
                    v-else
                    class="d-flex align-center"
                    style="gap: 6px"
                  >
                    <span class="text-body-2 font-weight-black error--text">
                      Not Set
                    </span>
                    <v-icon
                      x-small
                      color="error"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </div>
                </v-col>
              </v-row>
              <v-row
                dense
                class="d-flex justify-space-between align-center flex-wrap"
              >
                <v-col
                  cols="3"
                  sm="6"
                  md="6"
                  lg="3"
                  xl="3"
                >
                  <div @click.stop>
                    <StyledButton
                      key="call1"
                      preset="action-stacked"
                      icon="mdi-phone"
                      label="Call"
                      rounded="lg"
                      min-height="44px"
                      :text-transform="true"
                      :href="phoneHref(contact.phoneNumber)"
                      :disabled="!contact.phoneNumber"
                      style="width: 100% !important; min-width: 0% !important"
                    />
                  </div>
                </v-col>
                <v-col
                  cols="3"
                  sm="6"
                  md="6"
                  lg="3"
                  xl="3"
                >
                  <StyledButton
                    key="email"
                    preset="action-stacked"
                    icon="mdi-email"
                    label="Email"
                    rounded="lg"
                    min-height="44px"
                    :text-transform="true"
                    :action="
                      () => {
                        sendEmail(contact);
                      }
                    "
                    style="width: 100% !important; min-width: 0% !important"
                    :disabled="!contact.email"
                  />
                </v-col>
                <v-col
                  cols="3"
                  sm="6"
                  md="6"
                  lg="3"
                  xl="3"
                >
                  <StyledButton
                    key="invite"
                    preset="action-stacked"
                    icon="mdi-account-plus"
                    label="Invite"
                    rounded="lg"
                    min-height="44px"
                    :text-transform="true"
                    style="width: 100% !important; min-width: 0% !important"
                    :action="
                      () => {
                        $emit('inviteContact', contact);
                      }
                    "
                    :disabled="
                      !contact.email || !$route.name.includes('account') || true
                    "
                  />
                </v-col>
                <v-col
                  cols="3"
                  sm="6"
                  md="6"
                  lg="3"
                  xl="3"
                >
                  <StyledButton
                    key="info"
                    preset="action-stacked"
                    icon="mdi-account-circle"
                    label="Info"
                    rounded="lg"
                    min-height="44px"
                    :text-transform="true"
                    :to="{
                      name: 'contacts-view',
                      params: {
                        id: contact.id,
                      },
                    }"
                    style="width: 100% !important; min-width: 0% !important"
                  />
                </v-col>
              </v-row>
              <!-- <v-row dense>
                <v-col cols="12">
                  <v-btn
                    class="rounded-lg"
                    block
                    large
                    depressed
                    color="review white--text text-body-2 font-weight-bold"
                    style="text-transform: none !important"
                    key="view"
                    disabled
                  >
                    <v-icon left>mdi-calendar-clock</v-icon>
                    <span>Schedule a Call</span>
                  </v-btn>
                </v-col>
              </v-row> -->
            </v-container>
          </v-expand-transition>
        </v-list-item-content>
      </v-list-item>
      <v-divider
        v-if="index !== contacts.length - 1"
        style="width: 100%"
      />
    </div>
    <v-list-item
      v-else
      key="no-contacts"
      class="rounded-lg px-1"
    >
      <v-list-item-content>
        <v-list-item-subtitle
          class="text--disabled d-flex justify-center text-caption font-weight-bold"
        >
          No contacts found.
        </v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>
  </v-list>
</template>

<script>
// Libraries
import { mapGetters } from "vuex";
import {
  // Firestore
  firestore,
  collection,
  getDocs,
  where,
  query,
  getDoc,
  doc,
  // Functions
  functions,
  httpsCallable,
} from "@/firebase/init";
// Components
import StyledButton from "@/components/misc/ui/buttons/StyledButton.vue";

export default {
  name: "ContactsList",
  components: {
    StyledButton,
  },
  props: {
    account: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    parentLoading: {
      default: false,
      type: Boolean,
      required: false,
    },
  },
  data: () => ({
    loading: false,
    contacts: [],
  }),
  watch: {
    account: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length) {
          this.getContacts();
        }
      },
      deep: true,
    },
  },
  created() {
    this.setup();
  },
  methods: {
    /* API Calls */
    // GET
    async getContacts() {
      this.loading = true;
      this.contacts = [];
      let accountContacts = [],
        parentContacts = [];

      if (!this.account?.id) {
        this.loading = false;
        return;
      }

      const contacts = collection(firestore, "contacts");
      const account = where("relatedID", "==", this.account.id),
        active = where("active", "==", true);

      const q = query(contacts, account, active);
      const response = await getDocs(q);

      if (!response.empty) {
        accountContacts = response.docs
          .filter((item) => item.data().title !== "customer")
          .map((contact) => {
            return {
              id: contact.id,
              active: contact.data().active,
              text: `${contact.data().name.first} ${contact.data().name.last}`,
              value: contact.id,
              fullName: `${contact.data().name.first} ${
                contact.data().name.last
              }`,
              parent: false,
              ...contact.data(),
              expanded: false,
              title: contact.data().title
                ? this.getContactTitles.find(
                    (option) => option.value === contact.data().title
                  )
                : null,
            };
          })
          .sort((a, b) => (a.name.first > b.name.first ? 1 : -1));
      }

      if (this.account.parent) {
        const parentContactsRef = collection(firestore, "contacts");
        const parentQ = query(
          parentContactsRef,
          where("active", "==", true),
          where("relatedID", "==", this.account.parent)
        );
        const parentContactsResponse = await getDocs(parentQ);
        if (!parentContactsResponse.empty) {
          parentContacts = parentContactsResponse.docs
            .filter((item) => item.data().title !== "customer")
            .map((contact) => {
              return {
                id: contact.id,
                active: contact.data().active,
                value: contact.id,
                fullName: `${contact.data().name.first} ${
                  contact.data().name.last
                }`,
                parent: true,
                expanded: false,
                invite: false,
                emailing: false,
                calling: false,
                ...contact.data(),
                title: contact.data().title
                  ? this.getContactTitles.find(
                      (option) => option.value === contact.data().title
                    )
                  : null,
              };
            })
            .sort((a, b) => (a.name.first > b.name.first ? 1 : -1));
        }
      }
      this.contacts = [...accountContacts, ...parentContacts];
      this.loading = false;
    },
    // POST
    /* Main */
    async setup() {
      if (this.account) {
        this.getContacts();
      }
    },
    /* Misc */
    sendEmail(contact) {
      contact.emailing = true;
      if (contact.email?.length) window.open(`mailto:${contact.email}`);
      contact.emailing = false;
    },
    async inviteContact(contact) {
      console.log(contact);
      console.log(this.$route.name);
      return;
      contact.invite = true;
      const createProfile = httpsCallable(functions, "createMytraceProfile");
      let created = [];

      try {
        const response = await createProfile({
          user: {
            email: this.getUser.email,
            name: this.getUser.name,
            uid: this.getUser.uid,
          },
          contact: {
            name: contact.name,
            email: contact.email,
            title: contact.title.value,
            phoneNumber:
              contact.phoneNumber?.length > 0
                ? this.cleanedPhoneNumber(contact.phoneNumber)
                : "",
          },
          location: {
            country: contact.location.country,
            state: contact.location.state.abbreviation,
          },
          corporateEntity: {
            name: this.response.parent
              ? this.response.parent.name
              : this.response.account.name,
            phoneNumber: this.response.parent
              ? this.response.parent.phoneNumber
              : this.response.account.phoneNumber,
            email: this.response.parent
              ? this.response.parent.email
              : this.response.account.email,
          },
          storefront: {
            name: this.response.account.name,
            dba: this.response.account.dba,
            flags: this.response.account.flags,
            location: this.response.account.location,
            deliveryPreferences: this.response.account.deliveryPreferences,
            license: {
              number: this.response.account.license.number,
              expiration: this.response.account.license.expiration,
            },
            phoneNumber: this.response.account.phoneNumber,
            email: this.response.account.email,
            website: this.response.account.website,
          },
          brand: this.$options.filters.capitalize(this.brandText),
        });

        if (response.data.created.contact) {
          const contactName = `${contact.name.first} ${contact.name.last}`;
          created.push(
            `A login for ${this.$options.filters.capitalize(
              contactName
            )} was created`
          );
        }
        if (response.data.created.storefront) {
          if (response.data.created.corporateEntity) {
            created.push(
              `${this.$options.filters.capitalize(
                this.response.account.name
              )} was created as a new store`
            );
          } else {
            created.push(
              `${this.$options.filters.capitalize(
                this.response.account.name
              )} was appended to an existing corporate entity profile`
            );
          }
        }

        this.mytraceProfile = {
          active: true,
          color: created.length > 0 ? "green" : "orange",
          items: created,
        };
      } catch (error) {
        console.error(error.message);
        this.$root.toast.show({
          title: "There was an error processing your request.",
          color: "error",
          icon: "mdi-alert",
        });
      }

      contact.invite = false;
    },
  },
  computed: {
    ...mapGetters([
      "getAppName",
      "getUser",
      "getContactTitles",
      "getCountryCodes",
    ]),
  },
};
</script>

<style scoped>
.activeRotate .v-icon {
  transform: rotate(180deg);
}
</style>
